<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="loading"></p-progressBar>

<app-page-shell>
  <app-page-hero
    eyebrow="Fleet Accounting"
    title="Truck P&amp;L"
    subtitle="Single-truck management P&amp;L based on vehicle-dimension accounting lines and fixed-asset depreciation.">
    <div actions>
      <p-button icon="pi pi-refresh" label="Refresh" (onClick)="loadDashboard()" [loading]="loading"></p-button>
      <p-button icon="pi pi-cog" label="Asset Profile" severity="secondary" [outlined]="true" (onClick)="openProfileDialog()" [disabled]="!selectedVehicleId"></p-button>
    </div>
  </app-page-hero>

  <app-page-data-panel title="Truck And Period" subtitle="Truck is a profitability object, not a cost center.">
    <div class="filter-grid">
      <p-inputGroup>
        <p-inputGroupAddon><i class="fa-solid fa-truck"></i></p-inputGroupAddon>
        <p-iftaLabel>
          <p-select
            inputId="truckSelector"
            [options]="vehicleOptions"
            [(ngModel)]="selectedVehicleId"
            optionValue="vehicleId"
            [filter]="true"
            filterBy="plateNumber,registrationNumber,chassisNumber"
            appendTo="body"
            styleClass="w-full">
            <ng-template pTemplate="selectedItem" let-vehicle>
              <span>{{ selectedVehicleLabel() }}</span>
            </ng-template>
            <ng-template pTemplate="item" let-vehicle>
              <div class="vehicle-option">
                <strong>{{ vehicleLabel(vehicle) }}</strong>
                <span>{{ vehicle.resourceOwnershipType || 'OWNED' }} · {{ vehicle.resourceCompanyName || vehicle.transportationAgency?.agencyName || 'Current company' }}</span>
              </div>
            </ng-template>
          </p-select>
          <label for="truckSelector">Truck</label>
        </p-iftaLabel>
      </p-inputGroup>

      <p-inputGroup>
        <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
        <p-iftaLabel>
          <p-datepicker inputId="period" [(ngModel)]="dateRange" selectionMode="range" appendTo="body" styleClass="w-full"></p-datepicker>
          <label for="period">Period</label>
        </p-iftaLabel>
      </p-inputGroup>

      <p-inputGroup>
        <p-inputGroupAddon><i class="pi pi-book"></i></p-inputGroupAddon>
        <p-iftaLabel>
          <p-select inputId="basis" [options]="basisOptions" [(ngModel)]="basis" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
          <label for="basis">Basis</label>
        </p-iftaLabel>
      </p-inputGroup>

      <div class="filter-actions">
        <p-button label="Apply" icon="pi pi-filter" (onClick)="loadDashboard()" [disabled]="!selectedVehicleId"></p-button>
        <p-button label="Clear" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="clearFilters()"></p-button>
      </div>
    </div>
  </app-page-data-panel>

  <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

  <div class="dashboard-grid">
    <app-page-data-panel title="Asset Panel" subtitle="Purchase cost is capital invested; depreciation is the period expense.">
      <div class="asset-grid">
        <div>
          <span>Acquisition Cost</span>
          <strong>{{ money(dashboard?.asset?.acquisitionCost) }}</strong>
        </div>
        <div>
          <span>Accumulated Depreciation</span>
          <strong>{{ money(dashboard?.asset?.accumulatedDepreciation) }}</strong>
        </div>
        <div>
          <span>Net Book Value</span>
          <strong>{{ money(dashboard?.asset?.netBookValue) }}</strong>
        </div>
        <div>
          <span>Period Depreciation</span>
          <strong>{{ money(dashboard?.asset?.periodDepreciation) }}</strong>
          <p-tag [value]="pretty(dashboard?.asset?.depreciationSource)" [severity]="sourceSeverity(dashboard?.asset?.depreciationSource)"></p-tag>
        </div>
      </div>
    </app-page-data-panel>

    <app-page-data-panel title="Warnings" [resultText]="(dashboard?.warnings?.length || 0) + ' notices'">
      <div class="warning-list" *ngIf="dashboard?.warnings?.length; else noWarnings">
        <div class="warning-item" *ngFor="let warning of dashboard?.warnings">
          <i class="pi pi-info-circle"></i>
          <span>{{ warning }}</span>
        </div>
      </div>
      <ng-template #noWarnings>
        <div class="empty-state">No dashboard warnings for this period.</div>
      </ng-template>
    </app-page-data-panel>
  </div>

  <app-page-data-panel title="Monthly P&amp;L" subtitle="Revenue less operating cost, ownership cost, and explicit allocated overhead.">
    <div class="pnl-grid">
      <div class="pnl-column revenue">
        <h3>Revenue</h3>
        <div class="pnl-row" *ngFor="let row of bucketRows('REVENUE')">
          <span>{{ pretty(row.category) }}</span>
          <strong>{{ money(row.amount) }}</strong>
        </div>
        <div class="pnl-empty" *ngIf="!bucketRows('REVENUE').length">No tagged revenue.</div>
      </div>
      <div class="pnl-column operating">
        <h3>Operating Cost</h3>
        <div class="pnl-row" *ngFor="let row of bucketRows('OPERATING_COST')">
          <span>{{ pretty(row.category) }}</span>
          <strong>{{ money(row.amount) }}</strong>
        </div>
        <div class="pnl-empty" *ngIf="!bucketRows('OPERATING_COST').length">No tagged operating costs.</div>
      </div>
      <div class="pnl-column ownership">
        <h3>Ownership Cost</h3>
        <div class="pnl-row" *ngFor="let row of bucketRows('OWNERSHIP_COST')">
          <span>{{ pretty(row.category) }}</span>
          <strong>{{ money(row.amount) }}</strong>
        </div>
        <div class="pnl-empty" *ngIf="!bucketRows('OWNERSHIP_COST').length">No ownership costs.</div>
      </div>
      <div class="pnl-column overhead">
        <h3>Allocated Overhead</h3>
        <div class="pnl-row" *ngFor="let row of bucketRows('ALLOCATED_OVERHEAD')">
          <span>{{ pretty(row.category) }}</span>
          <strong>{{ money(row.amount) }}</strong>
        </div>
        <div class="pnl-empty" *ngIf="!bucketRows('ALLOCATED_OVERHEAD').length">No explicit allocations.</div>
      </div>
    </div>
  </app-page-data-panel>

  <app-page-data-panel title="Trend" subtitle="Monthly revenue, cost, and net truck profit.">
    <div class="trend-list">
      <div class="trend-row" *ngFor="let row of trendRows()">
        <div class="trend-period">{{ row.period }}</div>
        <div class="trend-bars">
          <div class="trend-bar revenue" [style.width]="trendWidth(row, 'revenue')"><span>{{ money(row.revenue) }}</span></div>
          <div class="trend-bar cost" [style.width]="trendWidth(row, 'operatingCost')"><span>{{ money(row.operatingCost) }}</span></div>
          <div class="trend-bar profit" [class.loss]="(row.netProfit || 0) < 0" [style.width]="trendWidth(row, 'netProfit')"><span>{{ money(row.netProfit) }}</span></div>
        </div>
      </div>
      <div class="empty-state" *ngIf="!trendRows().length">No trend rows yet.</div>
    </div>
  </app-page-data-panel>

  <app-page-data-panel title="Accounting Transactions" [resultText]="transactions().length + ' rows'">
    <p-table [value]="transactions()" [loading]="loading" [paginator]="true" [rows]="15" responsiveLayout="scroll" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '1100px' }">
      <ng-template pTemplate="header">
        <tr>
          <th>Date</th>
          <th>Document</th>
          <th>Account / Description</th>
          <th>Bucket</th>
          <th>Status</th>
          <th class="text-right">Amount</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-row>
        <tr>
          <td>{{ date(row.transactionDate) }}</td>
          <td>
            <strong>{{ row.transactionNumber || '-' }}</strong>
            <span class="muted">{{ row.sourceType || '-' }}</span>
          </td>
          <td>
            <strong>{{ row.accountName || row.description || '-' }}</strong>
            <span class="muted">{{ row.accountCode || row.sourceReference || row.description || '-' }}</span>
          </td>
          <td>
            <p-tag [value]="pretty(row.bucket)" [severity]="bucketSeverity(row.bucket)"></p-tag>
            <span class="muted">{{ pretty(row.category) }}</span>
          </td>
          <td><p-tag [value]="pretty(row.sourceStatus)" [severity]="sourceSeverity(row.sourceStatus)"></p-tag></td>
          <td class="text-right">{{ money(row.amount) }}</td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="6" class="empty-row">No Vehicle-dimension accounting rows were found for this truck.</td>
        </tr>
      </ng-template>
    </p-table>
  </app-page-data-panel>

  <app-page-data-panel title="Operational Reconciliation" subtitle="Unposted operational signals are supporting detail unless Operational Preview is selected." [resultText]="operationalSignals().length + ' signals'">
    <p-table [value]="operationalSignals()" [paginator]="true" [rows]="10" responsiveLayout="scroll" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '900px' }">
      <ng-template pTemplate="header">
        <tr>
          <th>Date</th>
          <th>Source</th>
          <th>Label</th>
          <th>Category</th>
          <th>Preview</th>
          <th class="text-right">Amount</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-row>
        <tr>
          <td>{{ date(row.signalDate) }}</td>
          <td>
            <strong>{{ row.sourceNumber || '-' }}</strong>
            <span class="muted">{{ row.sourceType }}</span>
          </td>
          <td>{{ row.label || '-' }}</td>
          <td>{{ pretty(row.category) }}</td>
          <td><p-tag [value]="row.includedInPreview ? 'Included' : 'Signal Only'" [severity]="row.includedInPreview ? 'warn' : 'secondary'"></p-tag></td>
          <td class="text-right">{{ money(row.amount) }}</td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="6" class="empty-row">No operational signals were found for this truck.</td>
        </tr>
      </ng-template>
    </p-table>
  </app-page-data-panel>
</app-page-shell>

<p-dialog header="Vehicle Accounting Profile" [(visible)]="profileDialogVisible" [modal]="true" [style]="{ width: '720px', maxWidth: '96vw' }">
  <div class="profile-form">
    <p-inputGroup>
      <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
      <p-iftaLabel>
        <p-datepicker inputId="acquisitionDate" [(ngModel)]="profileForm.acquisitionDate" appendTo="body" styleClass="w-full"></p-datepicker>
        <label for="acquisitionDate">Acquisition Date</label>
      </p-iftaLabel>
    </p-inputGroup>
    <p-inputGroup>
      <p-inputGroupAddon><i class="pi pi-money-bill"></i></p-inputGroupAddon>
      <p-iftaLabel>
        <p-inputNumber inputId="acquisitionCost" [(ngModel)]="profileForm.acquisitionCost" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2" styleClass="w-full"></p-inputNumber>
        <label for="acquisitionCost">Acquisition Cost</label>
      </p-iftaLabel>
    </p-inputGroup>
    <p-inputGroup>
      <p-inputGroupAddon><i class="pi pi-calendar-clock"></i></p-inputGroupAddon>
      <p-iftaLabel>
        <p-datepicker inputId="depreciationStartDate" [(ngModel)]="profileForm.depreciationStartDate" appendTo="body" styleClass="w-full"></p-datepicker>
        <label for="depreciationStartDate">Depreciation Start</label>
      </p-iftaLabel>
    </p-inputGroup>
    <p-inputGroup>
      <p-inputGroupAddon><i class="pi pi-clock"></i></p-inputGroupAddon>
      <p-iftaLabel>
        <p-inputNumber inputId="usefulLifeMonths" [(ngModel)]="profileForm.usefulLifeMonths" [min]="1" [maxFractionDigits]="0" styleClass="w-full"></p-inputNumber>
        <label for="usefulLifeMonths">Useful Life Months</label>
      </p-iftaLabel>
    </p-inputGroup>
    <p-inputGroup>
      <p-inputGroupAddon><i class="pi pi-wallet"></i></p-inputGroupAddon>
      <p-iftaLabel>
        <p-inputNumber inputId="salvageValue" [(ngModel)]="profileForm.salvageValue" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2" styleClass="w-full"></p-inputNumber>
        <label for="salvageValue">Salvage Value</label>
      </p-iftaLabel>
    </p-inputGroup>
    <p-inputGroup>
      <p-inputGroupAddon><i class="pi pi-sliders-h"></i></p-inputGroupAddon>
      <p-iftaLabel>
        <input id="depreciationMethod" pInputText [(ngModel)]="profileForm.depreciationMethod" class="w-full" />
        <label for="depreciationMethod">Depreciation Method</label>
      </p-iftaLabel>
    </p-inputGroup>
    <div class="notes-field">
      <p-iftaLabel>
        <textarea id="profileNotes" pTextarea rows="4" [(ngModel)]="profileForm.notes"></textarea>
        <label for="profileNotes">Notes</label>
      </p-iftaLabel>
    </div>
  </div>
  <ng-template pTemplate="footer">
    <p-button label="Cancel" severity="secondary" [outlined]="true" (onClick)="profileDialogVisible = false"></p-button>
    <p-button label="Save Profile" icon="pi pi-save" (onClick)="saveProfile()" [loading]="savingProfile"></p-button>
  </ng-template>
</p-dialog>
